
"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useTranslation } from 'react-i18next'
import { useToast } from '@/hooks/use-toast'
import useLLMQueue from "./useLLMQueue"
import { useAutoGenerationConfig } from './useAutoGenerationConfig'
import { useAutoGenerationAPI } from './useAutoGenerationAPI'
import { useAutoGenerationScheduler } from './useAutoGenerationScheduler'
import type { 
  UseAutoReportGenerationProps, 
  EditingState, 
  GenerationStatus 
} from '@/types/autoGeneration'

export const useAutoReportGeneration = ({
  analysisId,
  isRecording,
  lang,
  jwt,
  userEmail,
  config: userConfig = {},
}: UseAutoReportGenerationProps) => {
  // Only log on actual prop changes
  const prevProps = useRef({ analysisId, isRecording })
  
  if (prevProps.current.analysisId !== analysisId || prevProps.current.isRecording !== isRecording) {
    console.log('🔄 AUTO_GEN_HOOK: Hook initializing/re-initializing', {
      analysisId,
      isRecording,
      timestamp: new Date().toISOString()
    })
    prevProps.current = { analysisId, isRecording }
  }

  const { toast } = useToast()
  const { t } = useTranslation()

  // Initialize config - call hook at top level
  const config = useAutoGenerationConfig(userConfig)

  // States
  const [reportContent, setReportContent] = useState<{[key: string]: string}>({})
  const [editingState, setEditingState] = useState<EditingState>({})
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const [lastTitleGenerated, setLastTitleGenerated] = useState<Date | undefined>()
  const [lastReportGenerated, setLastReportGenerated] = useState<Date | undefined>()
  const [progressiveSections, setProgressiveSections] = useState<string[]>([])
  const [showProgressiveDisplay, setShowProgressiveDisplay] = useState(false)

  // Refs for stable values
  const transcriptionsRef = useRef<any[]>([])
  const recordingStartTime = useRef<Date | null>(null)
  const editingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isInitialized = useRef(false)

  // Initialize LLM Queue with time-based config - memoize to prevent recreations
  const {
    addToQueue,
    clearQueue,
    cancelCurrentCall, // Add cancellation support
    getQueueStatus,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    canMakeCall,
    metrics,
  } = useLLMQueue(config.timeBasedConfig, recordingStartTime.current)

  // Initialize API functions - memoize to prevent recreations
  const apiProps = useMemo(() => ({
    analysisId,
    lang,
    jwt,
    userEmail,
    editingState,
    transcriptionsRef
  }), [analysisId, lang, jwt, userEmail, editingState])

  const {
    canGenerateTitle,
    generateTitleAPI,
    generateReportAPI,
    titleError,
    reportError,
    setTitleError,
    setReportError
  } = useAutoGenerationAPI(apiProps)

  // Computed values - memoize to prevent recreations
  const isAnyEditing = useMemo(() => Object.values(editingState).some((editing) => editing), [editingState])
  const shouldPauseGeneration = useMemo(() => config.global.pauseOnEdit && isAnyEditing, [config.global.pauseOnEdit, isAnyEditing])

  // Initialize scheduler - memoize scheduler props to prevent recreations
  const schedulerProps = useMemo(() => ({
    isRecording,
    shouldPauseGeneration,
    config,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    canMakeCall,
    addToQueue,
    generateTitleAPI,
    generateReportAPI,
    transcriptionsRef,
    recordingStartTime,
    setIsGeneratingTitle,
    setIsGeneratingReport,
    setTitleError,
    setReportError,
    setLastTitleGenerated,
    setLastReportGenerated,
    setReportContent,
    setProgressiveSections,
    canGenerateTitle
  }), [
    isRecording,
    shouldPauseGeneration,
    config,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    canMakeCall,
    addToQueue,
    generateTitleAPI,
    generateReportAPI,
    setIsGeneratingTitle,
    setIsGeneratingReport,
    setTitleError,
    setReportError,
    setLastTitleGenerated,
    setLastReportGenerated,
    setReportContent,
    setProgressiveSections,
    canGenerateTitle
  ])

  const {
    scheduleNextGeneration,
    queueTitleGeneration,
    queueReportGeneration
  } = useAutoGenerationScheduler(schedulerProps)

  // Hook initialization/cleanup logging
  useEffect(() => {
    if (!isInitialized.current) {
      console.log('🟢 AUTO_GEN_HOOK: Hook mounted', { analysisId })
      isInitialized.current = true
    }
    
    return () => {
      console.log('🔴 AUTO_GEN_HOOK: Hook cleanup', { analysisId })
    }
  }, [analysisId])

  // Clean up editing state and timeouts when analysis changes
  useEffect(() => {
    // Clear editing state when analysis changes
    setEditingState({})
    
    // Clear any pending timeout when analysis changes
    if (editingTimeoutRef.current) {
      console.log('🧹 [AUTO_GEN] Clearing pending timeout due to analysis change')
      clearTimeout(editingTimeoutRef.current)
      editingTimeoutRef.current = null
    }
  }, [analysisId])

  // Handle recording state changes - only trigger when recording actually changes
  useEffect(() => {
    if (isRecording && !recordingStartTime.current) {
      console.log("🎙️ [AUTO_GEN] Recording started - initializing time-based scheduler")
      recordingStartTime.current = new Date()

      // Mostrar configuración de intervalos una sola vez
      config.timeBasedConfig.intervals.forEach((interval) => {
        console.log(
          `📅 [CONFIG] ${interval.description}: ${interval.maxCallsPerInterval} calls every ${interval.callIntervalSeconds}s`,
        )
      })

      // Iniciar scheduler
      scheduleNextGeneration()
    } else if (!isRecording && recordingStartTime.current) {
      console.log("🛑 [AUTO_GEN] Recording stopped - clearing scheduler and timeouts")
      recordingStartTime.current = null
      clearQueue()
      
      // Clear any pending editing timeout when recording stops
      if (editingTimeoutRef.current) {
        console.log('🧹 [AUTO_GEN] Clearing pending editing timeout due to recording stop')
        clearTimeout(editingTimeoutRef.current)
        editingTimeoutRef.current = null
      }
    }

    return () => {
      if (editingTimeoutRef.current) clearTimeout(editingTimeoutRef.current)
    }
  }, [isRecording, scheduleNextGeneration, clearQueue, config.timeBasedConfig.intervals])

  // Handle editing state changes with improved logic
  useEffect(() => {
    if (editingTimeoutRef.current) {
      clearTimeout(editingTimeoutRef.current)
      editingTimeoutRef.current = null
    }

    if (isAnyEditing) {
      console.log("✏️ [AUTO_GEN] User started editing - pausing generation and cancelling ongoing calls")
      // Cancel any ongoing API calls when editing starts
      cancelCurrentCall()
    } else {
      // Capture the current recording state when scheduling the timeout
      const currentIsRecording = isRecording
      
      editingTimeoutRef.current = setTimeout(() => {
        // Verify recording state hasn't changed since timeout was scheduled
        if (currentIsRecording && isRecording) {
          console.log("▶️ [AUTO_GEN] Grace period ended - resuming generation")
          scheduleNextGeneration()
        } else if (!isRecording) {
          console.log("⏸️ [AUTO_GEN] Grace period ended but recording is not active - no action needed")
        } else {
          console.log("🔄 [AUTO_GEN] Grace period ended but recording state changed - skipping resumption")
        }
        editingTimeoutRef.current = null
      }, config.global.editingGracePeriodSeconds * 1000)
      
      console.log(`⏳ [AUTO_GEN] Scheduled grace period timeout (${config.global.editingGracePeriodSeconds}s) - recording: ${currentIsRecording}`)
    }
  }, [isAnyEditing, isRecording, scheduleNextGeneration, config.global.editingGracePeriodSeconds, cancelCurrentCall])

  // Handle progressive sections display
  useEffect(() => {
    if (progressiveSections.length > 0 && !isGeneratingReport) {
      console.log('📊 [PROGRESSIVE] Starting progressive display for sections:', progressiveSections)
      setShowProgressiveDisplay(true)
    }
  }, [progressiveSections, isGeneratingReport])

  const handleProgressiveDisplayComplete = useCallback(() => {
    console.log('📊 [PROGRESSIVE] Progressive display completed')
    setShowProgressiveDisplay(false)
    setProgressiveSections([])
  }, [])

  // Public API - memoize callbacks to prevent recreations
  const updateTranscription = useCallback((transcriptions: any) => {
    console.log('📝 [AUTO_GEN] Transcriptions received:', transcriptions, 'Type:', typeof transcriptions)
    
    // Handle different transcription formats
    let processedTranscriptions: any[] = []
    
    if (Array.isArray(transcriptions)) {
      // If it's already an array, use it directly
      processedTranscriptions = transcriptions
    } else if (transcriptions && typeof transcriptions === 'object') {
      // If it's an object with utterances property, extract the utterances
      if (transcriptions.utterances && Array.isArray(transcriptions.utterances)) {
        processedTranscriptions = transcriptions.utterances
      } else {
        // If it's a single object, wrap it in an array
        processedTranscriptions = [transcriptions]
      }
    } else {
      // If it's not an object or array, log warning and use empty array
      console.warn('📝 [AUTO_GEN] Invalid transcription format received:', transcriptions)
      processedTranscriptions = []
    }
    
    transcriptionsRef.current = processedTranscriptions
    console.log(`📝 [AUTO_GEN] Transcriptions updated: ${processedTranscriptions.length} items, total length: ${processedTranscriptions.map(t => t.transcript || t.text || '').join(' ').length} chars`)
  }, [])

  const setEditing = useCallback((sectionId: string, editing: boolean) => {
    setEditingState((prev) => ({
      ...prev,
      [sectionId]: editing,
    }))
  }, [])

  const forceGeneration = useCallback(
    (type: "title" | "report" | "both") => {
      console.log(`🚀 [AUTO_GEN] Force generation requested: ${type}`)

      if (type === "title" || type === "both") {
        queueTitleGeneration()
      }

      if (type === "report" || type === "both") {
        queueReportGeneration()
      }
    },
    [queueTitleGeneration, queueReportGeneration],
  )

  const getGenerationStatus = useCallback((): GenerationStatus => {
    const queueStatus = getQueueStatus()
    const currentConfig = getCurrentTimeConfig()
    const nextInterval = getNextAvailableInterval()

    return {
      isGeneratingTitle,
      isGeneratingReport,
      queueStatus,
      metrics,
      lastTitleGenerated,
      lastReportGenerated,
      currentInterval: currentConfig,
      nextInterval,
      errors: {
        title: titleError,
        report: reportError,
      },
    }
  }, [
    isGeneratingTitle,
    isGeneratingReport,
    getQueueStatus,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    metrics,
    lastTitleGenerated,
    lastReportGenerated,
    titleError,
    reportError,
  ])

  return {
    // Content
    reportContent,

    // States
    editingState,
    isGeneratingTitle,
    isGeneratingReport,
    lastTitleGenerated,
    lastReportGenerated,
    titleError,
    reportError,
    
    // Progressive display
    progressiveSections,
    showProgressiveDisplay,
    onProgressiveDisplayComplete: handleProgressiveDisplayComplete,

    // Actions
    setEditing,
    updateTranscription,
    forceGeneration,
    getGenerationStatus,

    // Config (read-only)
    config,
  }
}
